import {
  Button,
  ColorCodes,
  Fab,
  IconIdentifier,
  Input,
  InputGroup,
  Placement,
  type SelectOption,
  toast,
  Tooltip,
} from '@aus-platform/design-system';
import { isNil, isUndefined } from 'lodash';
import React, {
  type ChangeEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { MapTool } from '../enums';
import { SearchBarValidationMessages } from './enums';
import {
  evaluateCoordinates,
  getCoordinateFieldsConfig,
  sanitizeNumericInput,
  WGS84_CRS_OPTION,
} from './helpers';
import {
  selectMap3DState,
  setCurrentActiveMapTool,
} from 'map-3d/shared/map-3d-slices';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import { type IPointTool, PointTool } from 'src/map-3d/shared';
import { useCRSInfo } from 'src/shared/api/crs';
import { CrsType, EPSGCode } from 'src/shared/enums';
import { registerCRSFromInfo } from 'src/shared/helpers/crs-helpers';
import { useCRS } from 'src/shared/hooks';

const WGS84_EPSG = String(EPSGCode.WGS84);

export const SearchBar: React.FC = () => {
  // Refs.
  const pointTool = useRef<IPointTool>();

  // Selectors.
  const { cesiumProxy, currentActiveMapTool } =
    useAppSelector(selectMap3DState);

  // Dispatchers.
  const dispatch = useAppDispatch();

  // States.
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedCrs, setSelectedCrs] =
    useState<SelectOption>(WGS84_CRS_OPTION);
  const [latitudeOrEasting, setLatitudeOrEasting] = useState('');
  const [longitudeOrNorthing, setLongitudeOrNorthing] = useState('');
  // Bumped when a CRS definition is registered, to re-run validation.
  const [registeredCrsToken, setRegisteredCrsToken] = useState(WGS84_EPSG);

  // Derived CRS details.
  const selectedEpsg = String(selectedCrs.value);
  const isWgs84 = selectedEpsg === WGS84_EPSG;

  // Apis / hooks.
  const { crsSelectOptions, isLoadingCrsList } = useCRS(undefined, [
    CrsType.Geographic,
    CrsType.Projected,
  ]);
  const {
    data: crsInfo,
    isError: isCrsInfoError,
    isFetching: isFetchingCrsInfo,
  } = useCRSInfo(selectedEpsg, isExpanded && !isWgs84);

  const isGeographic = isWgs84 ? true : (crsInfo?.isGeographic ?? false);

  const fieldsConfig = useMemo(
    () => getCoordinateFieldsConfig(isGeographic),
    [isGeographic],
  );

  const crsSelectOptionsWithDefault = useMemo(
    () =>
      crsSelectOptions.length > 0 ? crsSelectOptions : [WGS84_CRS_OPTION],
    [crsSelectOptions],
  );

  const evaluation = useMemo(
    () =>
      evaluateCoordinates({
        isGeographic,
        epsgCode: selectedEpsg,
        latitudeOrEasting,
        longitudeOrNorthing,
        range: {
          eastingMin: crsInfo?.eastingMin ?? null,
          eastingMax: crsInfo?.eastingMax ?? null,
          northingMin: crsInfo?.northingMin ?? null,
          northingMax: crsInfo?.northingMax ?? null,
        },
      }),
    // registeredCrsToken forces re-evaluation once a CRS becomes available.
    [
      isGeographic,
      selectedEpsg,
      latitudeOrEasting,
      longitudeOrNorthing,
      registeredCrsToken,
      crsInfo?.eastingMin,
      crsInfo?.eastingMax,
      crsInfo?.northingMin,
      crsInfo?.northingMax,
    ],
  );

  // useEffects.
  useEffect(() => {
    if (cesiumProxy?.cesiumViewer && isUndefined(pointTool.current)) {
      pointTool.current = new PointTool(cesiumProxy.cesiumViewer);
    }

    return () => {
      pointTool.current?.destroy();
      pointTool.current = undefined;
    };
  }, [cesiumProxy?.cesiumViewer]);

  useEffect(() => {
    if (currentActiveMapTool !== MapTool.SearchTool) {
      clearSearch();
    }
  }, [currentActiveMapTool]);

  useEffect(() => {
    if (isWgs84 || isNil(crsInfo)) {
      return;
    }
    try {
      registerCRSFromInfo(crsInfo);
      setRegisteredCrsToken(String(crsInfo.srid));
    } catch {
      toast.error(SearchBarValidationMessages.CrsRegistrationFailed);
    }
  }, [crsInfo, isWgs84]);

  useEffect(() => {
    if (isCrsInfoError) {
      toast.error(SearchBarValidationMessages.CrsRegistrationFailed);
    }
  }, [isCrsInfoError]);

  // Handlers.
  const onClickSearchBarButton = () => {
    if (isExpanded) {
      clearSearch();
      dispatch(setCurrentActiveMapTool(MapTool.None));
    } else {
      setIsExpanded(true);
      dispatch(setCurrentActiveMapTool(MapTool.SearchTool));
    }
  };

  const onChangeLatitudeOrEasting = (event: ChangeEvent<HTMLInputElement>) => {
    setLatitudeOrEasting(
      sanitizeNumericInput(
        event.currentTarget.value,
        fieldsConfig.primaryAllowsNegative,
      ),
    );
  };

  const onChangeLongitudeOrNorthing = (event: ChangeEvent<HTMLInputElement>) => {
    setLongitudeOrNorthing(
      sanitizeNumericInput(
        event.currentTarget.value,
        fieldsConfig.secondaryAllowsNegative,
      ),
    );
  };

  const onChangeCrs = (newCrs: SelectOption | null) => {
    if (isNil(newCrs)) {
      return;
    }
    // Switching CRS clears inputs and resets error states (Scenario 7).
    setSelectedCrs(newCrs);
    setLatitudeOrEasting('');
    setLongitudeOrNorthing('');
  };

  const onGoToPoint = () => {
    if (!evaluation.isValid || isNil(evaluation.coordinates)) {
      return;
    }
    const { longitude, latitude } = evaluation.coordinates;
    pointTool.current?.deleteAllPoints();
    addPointWithStyle(longitude, latitude);
    cesiumProxy?.flyTo({
      longitude: longitude.toString(),
      latitude: latitude.toString(),
      height: 5000,
    });
  };

  const onKeyDownHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case 'Enter':
        onGoToPoint();
        break;
      case 'Escape':
        clearSearch();
        break;
      default:
        break;
    }
  };

  // Helpers.
  const clearSearch = () => {
    setIsExpanded(false);
    setSelectedCrs(WGS84_CRS_OPTION);
    setLatitudeOrEasting('');
    setLongitudeOrNorthing('');
    pointTool.current?.deleteAllPoints();
  };

  const addPointWithStyle = (longitude: number, latitude: number) => {
    const point = pointTool.current?.addPoint(longitude, latitude);
    point?.updateStyle({
      pointStyleOptions: {
        color: '#CA4139',
        strokeColor: ColorCodes.White,
        strokeWidth: 1.4,
      },
    });
  };

  return (
    <div className="search-bar">
      <Tooltip hoverText="Search Coordinates" placement={Placement.Right}>
        <Fab
          leftIconIdentifier={IconIdentifier.Search}
          onClick={onClickSearchBarButton}
          dataTestId="map-tools-search-bar-icon-btn"
          active={isExpanded}
        />
      </Tooltip>
      {isExpanded && (
        <div className="search-bar__panel" data-testid="map-tools-search-bar">
          <InputGroup className="search-bar__crs-group">
            <Input.Label>Coordinate system</Input.Label>
            <Input.Select
              className="search-bar__crs-select"
              options={crsSelectOptionsWithDefault}
              value={selectedCrs}
              onChange={onChangeCrs}
              noOptionsMessage={() => 'No Options'}
              isLoading={isLoadingCrsList}
              placeholder="Select coordinate system"
            />
          </InputGroup>
          <div className="search-bar__fields">
            <InputGroup>
              <Input.Label>{fieldsConfig.primaryLabel}</Input.Label>
              <Input.Text
                inputMode="decimal"
                value={latitudeOrEasting}
                onChange={onChangeLatitudeOrEasting}
                onKeyDown={onKeyDownHandler}
                isInvalid={evaluation.latitudeOrEastingInvalid}
                placeholder={fieldsConfig.primaryLabel}
                dataTestId="map-tools-search-bar-first-input"
              />
            </InputGroup>
            <InputGroup>
              <Input.Label>{fieldsConfig.secondaryLabel}</Input.Label>
              <Input.Text
                inputMode="decimal"
                value={longitudeOrNorthing}
                onChange={onChangeLongitudeOrNorthing}
                onKeyDown={onKeyDownHandler}
                isInvalid={evaluation.longitudeOrNorthingInvalid}
                placeholder={fieldsConfig.secondaryLabel}
                dataTestId="map-tools-search-bar-second-input"
              />
            </InputGroup>
          </div>
          <Button
            className="search-bar__go-to-point"
            onClick={onGoToPoint}
            disabled={!evaluation.isValid}
            isLoading={isFetchingCrsInfo}
          >
            Go to point
          </Button>
        </div>
      )}
    </div>
  );
};
